import { type ComponentProps, useMemo, useState } from 'react';
import { FieldInfo, hasFieldError, useFieldContext, useIsFieldOptional } from './shared';
import { useFormReadOnly } from './form-read-only';
import { FieldLabelRow } from './field-label-row';
import { getTranslatedCountry, type Locale } from '@worldcoo/monolith/core';
import { countries } from '@worldcoo/monolith/forms/countries';
import {
	Combobox,
	ComboboxContent,
	ComboboxEmpty,
	ComboboxGroup,
	ComboboxInput,
	ComboboxItem,
	ComboboxLabel,
	ComboboxList,
	ComboboxSeparator,
	ComboboxTrigger,
} from '../combobox';
import { Flag, countryCodeToLocale } from './country-flags';
import { cn } from '../../lib/utils';

type CountryOption = { value: string; label: string };

export type CountryFieldTexts = {
	search: string;
	popular: string;
	allCountries: string;
	noCountryFound: string;
};

const priorityCountries = countries.filter((c) => c.priority === 1);
const nonPriorityCountries = countries.filter((c) => c.priority >= 2);

function CountryFlag({ code }: { code: string }) {
	const locale = countryCodeToLocale[code];
	if (!locale) return null;
	return <Flag locale={locale} className="size-5 shrink-0" />;
}

export default function CountryField({
	label,
	locale = 'en',
	className,
	texts,
	optional,
}: ComponentProps<'div'> & {
	label: string;
	locale?: Locale;
	variant?: 'default' | 'outline';
	texts: CountryFieldTexts;
	optional?: boolean;
}) {
	const field = useFieldContext<string>();
	const formReadOnly = useFormReadOnly();
	const isOptional = useIsFieldOptional(field.name, optional);
	const [searchValue, setSearchValue] = useState('');

	const priorityItems = useMemo<CountryOption[]>(
		() =>
			priorityCountries.map((c) => ({
				value: c.country,
				label: getTranslatedCountry(c.country, locale) ?? c.name,
			})),
		[locale],
	);

	const otherItems = useMemo<CountryOption[]>(
		() =>
			nonPriorityCountries.map((c) => ({
				value: c.country,
				label: getTranslatedCountry(c.country, locale) ?? c.name,
			})),
		[locale],
	);

	const allItems = useMemo(() => [...priorityItems, ...otherItems], [priorityItems, otherItems]);

	const selectedItem = useMemo(
		() => allItems.find((i) => i.value === field.state.value) ?? null,
		[allItems, field.state.value],
	);

	const filteredPriorityItems = useMemo(() => {
		if (!searchValue) return priorityItems;
		const search = searchValue.toLowerCase();
		return priorityItems.filter((item) => item.label.toLowerCase().includes(search));
	}, [priorityItems, searchValue]);

	const filteredOtherItems = useMemo(() => {
		if (!searchValue) return otherItems;
		const search = searchValue.toLowerCase();
		return otherItems.filter((item) => item.label.toLowerCase().includes(search));
	}, [otherItems, searchValue]);

	return (
		<div
			className={cn(
				'flex flex-col gap-2',
				className,
				'[&_button]:transition-[color,border-color,padding,box-shadow]',
				formReadOnly &&
					'pointer-events-none [&_button]:border-transparent [&_button]:px-0 [&_button]:focus-visible:ring-0 [&_[data-slot=combobox-trigger]>svg]:hidden',
			)}
		>
			{label && (
				<FieldLabelRow
					label={label}
					htmlFor={field.name}
					hasError={field.state.meta.isTouched && !field.state.meta.isValid}
					optional={isOptional}
				/>
			)}
			<Combobox
				items={allItems}
				value={selectedItem}
				onValueChange={(item: CountryOption | null) => field.handleChange(item?.value ?? '')}
				onInputValueChange={setSearchValue}
				isItemEqualToValue={(a: CountryOption, b: CountryOption) => a.value === b.value}
			>
				<ComboboxTrigger
					render={
						<button
							id={field.name}
							type="button"
							aria-invalid={hasFieldError(field.state.meta)}
							className={cn(
								'dark:bg-input/30 border-input focus-visible:border-ring aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive aria-invalid:ring-3 flex h-8 w-full items-center justify-between rounded-lg border bg-transparent px-2.5 text-sm transition-[color,border-color,padding,box-shadow] outline-none',
								!selectedItem && 'text-muted-foreground',
							)}
						/>
					}
				>
					{selectedItem ? (
						<span className="flex items-center gap-2">
							<CountryFlag code={selectedItem.value} />
							{selectedItem.label}
						</span>
					) : (
						<span />
					)}
				</ComboboxTrigger>
				<ComboboxContent>
					<ComboboxInput showTrigger={false} placeholder={texts.search} />
					<ComboboxList>
						{filteredPriorityItems.length > 0 && (
							<ComboboxGroup>
								<ComboboxLabel>{texts.popular}</ComboboxLabel>
								{filteredPriorityItems.map((item) => (
									<ComboboxItem key={item.value} value={item}>
										<CountryFlag code={item.value} />
										{item.label}
									</ComboboxItem>
								))}
							</ComboboxGroup>
						)}
						{filteredPriorityItems.length > 0 && filteredOtherItems.length > 0 && <ComboboxSeparator />}
						{filteredOtherItems.length > 0 && (
							<ComboboxGroup>
								<ComboboxLabel>{texts.allCountries}</ComboboxLabel>
								{filteredOtherItems.map((item) => (
									<ComboboxItem key={item.value} value={item}>
										<CountryFlag code={item.value} />
										{item.label}
									</ComboboxItem>
								))}
							</ComboboxGroup>
						)}
					</ComboboxList>
					<ComboboxEmpty>{texts.noCountryFound}</ComboboxEmpty>
				</ComboboxContent>
			</Combobox>
			<FieldInfo fieldState={field.state} />
		</div>
	);
}
